import { useApolloClient, useMutation } from '@apollo/client';
import IconLock from '@apollo/icons/default/IconLock.svg';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { LinkEndpointModalData } from 'src/app/graph/explorerPage/connectionSettingsModal/ConnectionSettingsModal';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import {
  DeleteVariantMutation,
  isContractDisabled,
} from '../../graphSettingsPage/variantsPage/SourceVariantsPage';
import { variantSettingsRouteConfig } from '../../routes';

export const ContractVariantsTable = ({
  graph,
  contractsEnabled,
}: {
  graph: GraphQLTypes.VariantSettings__ContractsPage_graph;
  contractsEnabled: boolean;
}) => {
  const graphRef = useGraphRef();

  const contractVariants = graph.variants.filter(
    (variant) =>
      variant.isContract &&
      variant.sourceVariant?.name === graphRef?.graphVariant,
  );

  const { isDisabled, disabledReason } = isContractDisabled(
    !!contractsEnabled,
    graph.variant?.supportedDirectives || [],
  );

  return (
    <>
      {isDisabled ? (
        <Alert status="warning">{disabledReason}</Alert>
      ) : contractVariants.length === 0 ? (
        <div
          data-analytics-category="Contracts"
          data-analytics-label="Settings: CTA: no-contracts"
        >
          This variant has no contracts.
        </div>
      ) : (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th className="w-1/5">Name</Th>
                <Th isNumeric className="w-[5%]">
                  Types
                </Th>
                <Th className="w-[5%]">Included tags</Th>
                <Th className="w-[5%]">Excluded tags</Th>
                <Th className="w-[15%]">
                  <span className="sr-only">Actions</span>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {contractVariants.map((variant) => {
                const { activeSchemaPublish, contractFilterConfig } = variant;

                return (
                  <Tr key={variant.id}>
                    <Td>
                      <Link
                        className="group flex items-center gap-2"
                        to={variantSettingsRouteConfig.path({
                          graphId: graph.id,
                          graphVariant: variant.name,
                          tab: 'general',
                        })}
                      >
                        {variant.name}
                        {variant.hasManagedSubgraphs && (
                          <Badge>Federated</Badge>
                        )}
                        {variant.isPublic && <Badge>Public</Badge>}
                        {variant.isProtected && (
                          <Badge variant="warn" icon={<IconLock />}>
                            Protected
                          </Badge>
                        )}
                      </Link>
                    </Td>
                    <Td isNumeric>
                      {activeSchemaPublish?.schema.typeCount.toLocaleString()}
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        {contractFilterConfig?.include.map((tag) => (
                          <Badge key={tag}>{tag}</Badge>
                        ))}
                      </div>
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        {contractFilterConfig?.exclude.map((tag) => (
                          <Badge key={tag}>{tag}</Badge>
                        ))}
                      </div>
                    </Td>
                    <Td isActionsMenu>
                      <ContractVariantKebabMenu
                        canManageVariants={graph.roles?.canManageVariants}
                        graphId={graph.id}
                        variant={variant}
                        canManageContracts={!isDisabled}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export const ContractVariantKebabMenu = ({
  canManageVariants,
  graphId,
  variant: { name, sourceVariant },
  canManageContracts,
}: {
  canManageVariants?: boolean;
  graphId: string;
  variant: GraphQLTypes.VariantSettings__ContractsPage_graph_variants;
  canManageContracts: boolean;
}) => {
  const location = useLocation<{
    linkEndpointModalData: LinkEndpointModalData;
  }>();

  const client = useApolloClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [deleteTag, { loading, error }] = useMutation(DeleteVariantMutation, {
    variables: { serviceId: graphId || '', schemaTag: name || '' },
    onCompleted: (data) => {
      setDeleteModalOpen(false);
      client.cache.evict({
        id: client.cache.identify({
          __typename: data.service?.service.__typename,
          id: data.service?.service.id,
        }),
      });
    },
  });

  return (
    <>
      <Menu placement="bottom-end" isLazy autoSelect={false}>
        <Tooltip
          disabled={canManageVariants}
          label="You do not have permission to manage contracts"
        >
          <MenuButton
            as={IconButton}
            isDisabled={!canManageVariants}
            variant="secondary"
          >
            <IconOverflow />
          </MenuButton>
        </Tooltip>
        <MenuContent>
          <MenuList>
            <MenuItem
              as={Link}
              to={setOverlayVisibleLocation({
                location,
                overlayName: Config.modals.viewContractDetails,
                additionalSearchParameters: {
                  [Config.queryParameters.ContractVariant]: name,
                },
              })}
            >
              View Configuration
            </MenuItem>
            {canManageContracts && (
              <MenuItem
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.editContractConfigWizard,
                  additionalSearchParameters: {
                    [Config.queryParameters.ContractVariant]: name,
                    [Config.queryParameters.SourceVariant]: sourceVariant?.name,
                  },
                })}
              >
                Edit Contract
              </MenuItem>
            )}
            <MenuItem
              className="text-error hover:bg-error hover:text-error"
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete Contract
            </MenuItem>
          </MenuList>
        </MenuContent>
      </Menu>

      {deleteModalOpen && (
        <Modal isOpen onClose={() => setDeleteModalOpen(false)} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                Are you sure you want to delete {name.toUpperCase()}?
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <em>This action cannot be undone.</em>
              {!!error && <ErrorMessage error={error} />}
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  isLoading={loading}
                  loadingText="Yes, Delete"
                  onClick={() => deleteTag()}
                >
                  Yes, Delete
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
